import React, { useMemo } from "react";
import { Card, Avatar, Typography, Tag, Button, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import {
  ProfileOutlined,
  FileTextOutlined,
  MedicineBoxOutlined,
} from "@ant-design/icons";
import { useMovil } from "../../../../hooks/useIsNarrow";
import { FilaTocable } from "../../../revi/FilaTocable";
import { CardActionsMenu } from "../CardActionsMenu";
import "./PatientCardLite.scss";

const { Text } = Typography;

function calcAge(birthdate) {
  if (!birthdate) return null;
  const d = new Date(birthdate);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
  return age;
}

// La etiqueta del sexo se traduce, pero el codigo que llega de la BD ("M", "F",
// "O") no se toca: es el dato clinico. Por eso la funcion recibe el traductor en
// vez de devolver texto fijo.
function sexoLabel(t, sexo) {
  if (sexo === "M") return t("pacientes.fichaLite.sexo.hombre");
  if (sexo === "F") return t("pacientes.fichaLite.sexo.mujer");
  if (sexo === "O") return t("pacientes.fichaLite.sexo.otro");
  return "—";
}

function hasClinicalInfo(info) {
  if (!info) return false;
  const keys = [
    "referral_info",
    "diagnosis",
    "medical_history",
    "medications",
    "functional_status",
    "observations",
    "goals",
  ];
  return keys.some(
    (k) => typeof info[k] === "string" && info[k].trim().length > 0,
  );
}

export function PatientCardLite({ patient, onView, onEvaluations }) {
  // "Paciente" no se repite aqui: ya vive en el bloque comun de roles.
  const { t } = useTranslation(["ui", "common"]);

  const fullName = useMemo(
    () => `${patient?.name || ""} ${patient?.surname || ""}`.trim(),
    [patient?.name, patient?.surname],
  );

  const age = useMemo(() => calcAge(patient?.birthdate), [patient?.birthdate]);
  const hasAnamnesis = useMemo(
    () => hasClinicalInfo(patient?.clinical_info),
    [patient?.clinical_info],
  );

  const movil = useMovil();

  /**
   * En movil, fila simple. Los dos botones a ancho completo de abajo -unos 100
   * px solo de acciones- pasan al menu, y el estado clinico deja de ser una
   * etiqueta de color para ser texto legible junto al sexo y la edad.
   *
   * Tocar la fila abre la ficha, que es lo que el terapeuta busca el 90 % de
   * las veces.
   */
  if (movil) {
    const meta = [
      sexoLabel(t, patient?.sexo),
      age !== null
        ? t("common:contadores.anios", { count: age })
        : t("pacientes.fichaLite.edadDesconocida"),
      hasAnamnesis ? t("pacientes.fichaLite.anamnesis") : null,
    ].filter(Boolean);

    return (
      <FilaTocable
        as={Card}
        alto={88}
        className="revi-patient-card revi-patient-card--fila"
        onTocar={() => onView(patient?._id)}
        aria-label={fullName || t("common:roles.patient")}
        hoverable={false}
        kebab={
          <CardActionsMenu
            ariaLabel={fullName}
            items={[
              {
                key: "ficha",
                icon: <ProfileOutlined />,
                label: t("pacientes.fichaLite.verFicha"),
                run: () => onView(patient?._id),
              },
              {
                key: "evals",
                icon: <FileTextOutlined />,
                label: t("pacientes.fichaLite.evaluaciones"),
                run: () => onEvaluations(patient?._id),
              },
            ]}
          />
        }
      >
        <div className="revi-patient-card__fila">
          {/* Sin foto no se pinta un circulo gris vacio: no aporta nada y come
              48 px de ancho en la unica fila que hay. */}
          {patient?.avatar ? <Avatar size={40} src={patient.avatar} /> : null}

          <div className="revi-patient-card__filaTexto">
            <span className="revi-patient-card__filaNombre">
              {fullName || t("common:roles.patient")}
            </span>
            <span className="revi-patient-card__filaMeta">
              {meta.join(" \u00b7 ")}
            </span>
          </div>
        </div>
      </FilaTocable>
    );
  }

  return (
    <Card className="revi-patient-card" hoverable>
      <div className="revi-patient-card__head">
        <Avatar size={52} src={patient?.avatar} />
        <div className="revi-patient-card__meta">
          <Text className="revi-patient-card__name">
            {fullName || t("common:roles.patient")}
          </Text>
          <div className="revi-patient-card__badges">
            <Tag variant="filled">{sexoLabel(t, patient?.sexo)}</Tag>
            {/* Contador: en frances el cero y el uno van en singular. */}
            <Tag variant="filled">
              {age !== null
                ? t("common:contadores.anios", { count: age })
                : t("pacientes.fichaLite.edadDesconocida")}
            </Tag>
            {hasAnamnesis && (
              <Tooltip title={t("pacientes.fichaLite.anamnesisTooltip")}>
                <Tag color="green" icon={<MedicineBoxOutlined />}>
                  {t("pacientes.fichaLite.anamnesis")}
                </Tag>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      <div className="revi-patient-card__actions">
        <Button
          icon={<ProfileOutlined />}
          onClick={() => onView(patient?._id)}
          block
        >
          {t("pacientes.fichaLite.verFicha")}
        </Button>

        <Button
          icon={<FileTextOutlined />}
          onClick={() => onEvaluations(patient?._id)}
          block
        >
          {t("pacientes.fichaLite.evaluaciones")}
        </Button>
      </div>
    </Card>
  );
}
